/**
 * AamvaSchedulePanel — what AAMVA charges us per NMVTIS record, by effective date, plus the monthly minimum and
 * service fee. When AAMVA publishes new fees, add a schedule with its start date: earlier months keep costing at
 * the schedule that was in force for them.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Save, Trash2, Receipt } from 'lucide-react';
import { getErrorMessage } from '../../../services/apiService';
import { BandRow, toBandItems, validateBandRows } from '../../../services/pricingTiersService';
import { AamvaScheduleResponse, bandsToRows, getAamvaSchedule, saveAamvaSchedule } from '../../../services/marginsService';

interface Props {
  onSaved: () => void;
}

const AamvaSchedulePanel: React.FC<Props> = ({ onSaved }) => {
  const [data, setData] = useState<AamvaScheduleResponse | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [rows, setRows] = useState<BandRow[]>([]);
  const [minimum, setMinimum] = useState('');
  const [serviceFee, setServiceFee] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const pick = useCallback((d: AamvaScheduleResponse, date: string) => {
    const schedule = d.schedules.find((s) => s.effectiveFrom.slice(0, 10) === date);
    setEffectiveFrom(date);
    setRows(schedule ? bandsToRows(schedule.bands) : []);
  }, []);

  const load = useCallback(async () => {
    try {
      const d = await getAamvaSchedule();
      setData(d);
      setMinimum(String(d.monthlyMinimum));
      setServiceFee(String(d.monthlyServiceFee));
      if (d.schedules.length > 0) pick(d, d.schedules[0].effectiveFrom.slice(0, 10));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, [pick]);

  useEffect(() => { load(); }, [load]);

  const setRow = (i: number, patch: Partial<BandRow>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => {
    const last = rows[rows.length - 1];
    const nextMin = last && last.maxQuantity.trim() !== '' ? String(parseInt(last.maxQuantity, 10) + 1) : rows.length === 0 ? '1' : '';
    setRows([...rows, { minQuantity: nextMin, maxQuantity: '', ratePerUnit: '', isActive: true, description: '' }]);
  };

  const save = async () => {
    const invalid = !effectiveFrom ? 'Pick the date this schedule takes effect.' : rows.length === 0 ? 'Add at least one band.' : validateBandRows(rows);
    const min = parseFloat(minimum);
    const fee = parseFloat(serviceFee);
    if (invalid) { setError(invalid); return; }
    if (!Number.isFinite(min) || min < 0 || !Number.isFinite(fee) || fee < 0) { setError('Minimum and service fee must be numbers of 0 or more.'); return; }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const d = await saveAamvaSchedule({ effectiveFrom, bands: toBandItems(rows), monthlyMinimum: min, monthlyServiceFee: fee });
      setData(d);
      pick(d, effectiveFrom);
      setSuccess(`AAMVA schedule effective ${effectiveFrom} saved.`);
      onSaved();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-card overflow-hidden">
      <div className="px-6 py-4 border-b border-black/[0.06]">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-2">
          <Receipt className="w-4 h-4 text-[#c41e3a]" /> What AAMVA charges us
        </h2>
      </div>

      <div className="px-6 py-5 space-y-4">
        {!data && !error && <div className="text-[#64748b]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}

        {data && (
          <>
            <div className="flex flex-wrap items-end gap-4">
              <label className="text-xs text-[#64748b]">
                Schedule
                <select
                  value={data.schedules.some((s) => s.effectiveFrom.slice(0, 10) === effectiveFrom) ? effectiveFrom : ''}
                  onChange={(e) => (e.target.value ? pick(data, e.target.value) : (setEffectiveFrom(''), setRows([])))}
                  className="block mt-1 px-2 py-1.5 rounded border border-black/[0.12] bg-white text-sm text-[#0f172a]"
                >
                  {data.schedules.map((s) => (
                    <option key={s.effectiveFrom} value={s.effectiveFrom.slice(0, 10)}>Effective {s.effectiveFrom.slice(0, 10)}</option>
                  ))}
                  <option value="">New schedule…</option>
                </select>
              </label>
              <label className="text-xs text-[#64748b]">
                Effective from
                <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                  className="block mt-1 px-2 py-1 rounded border border-black/[0.12] bg-white text-sm text-[#0f172a]" />
              </label>
              <label className="text-xs text-[#64748b]">
                Monthly minimum ($)
                <input value={minimum} onChange={(e) => setMinimum(e.target.value)} inputMode="decimal"
                  className="block mt-1 w-28 px-2 py-1 rounded border border-black/[0.12] bg-white text-sm text-right" />
              </label>
              <label className="text-xs text-[#64748b]">
                Monthly service fee ($)
                <input value={serviceFee} onChange={(e) => setServiceFee(e.target.value)} inputMode="decimal"
                  className="block mt-1 w-28 px-2 py-1 rounded border border-black/[0.12] bg-white text-sm text-right" />
              </label>
            </div>

            <table className="w-full text-sm max-w-xl">
              <thead className="text-xs uppercase tracking-wider text-[#94a3b8] border-b border-black/[0.06]">
                <tr><th className="text-left py-2">From record</th><th className="text-left">To record</th><th className="text-right">Per record</th><th /></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1"><input value={r.minQuantity} onChange={(e) => setRow(i, { minQuantity: e.target.value })} className="w-28 px-2 py-1 rounded border border-black/[0.12] bg-white" /></td>
                    <td><input value={r.maxQuantity} placeholder="and up" onChange={(e) => setRow(i, { maxQuantity: e.target.value })} className="w-28 px-2 py-1 rounded border border-black/[0.12] bg-white" /></td>
                    <td className="text-right"><input value={r.ratePerUnit} onChange={(e) => setRow(i, { ratePerUnit: e.target.value })} inputMode="decimal" className="w-24 px-2 py-1 rounded border border-black/[0.12] bg-white text-right" /></td>
                    <td className="text-right"><button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-[#94a3b8] hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={addRow} className="text-xs text-[#475569] hover:text-[#c41e3a] inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add band</button>

            <p className="text-xs text-[#64748b]">
              Graduated on our total monthly records across all clients. AAMVA bills the greater of the record charges and
              the minimum, plus the service fee. This only feeds the margin figures; it does not change any client's bill.
            </p>
          </>
        )}

        {error && <div className="p-3 rounded-lg border bg-red-50 border-red-200 text-sm text-red-800">{error}</div>}
        {success && <div className="p-3 rounded-lg border bg-emerald-50 border-emerald-200 text-sm text-emerald-800">{success}</div>}

        {data && (
          <div className="flex justify-end">
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830] disabled:opacity-40">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save AAMVA schedule
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AamvaSchedulePanel;
